// Add Renderer2 back to the import
import {
  Component,
  OnInit,
  ElementRef,
  AfterViewInit,
  ViewChild,
  OnDestroy,
  QueryList,
  ViewChildren,
  Renderer2,
  HostBinding,
  ChangeDetectionStrategy
} from '@angular/core';
import { TokenStorageService } from '@app/shared/services/token-storage.service';
import { CustomUtils } from '@app/shared/utils/custom-utils';
import { FormControl } from '@angular/forms';
import { LayoutService } from '@app/shared/services/layout.service';
import { Subscription } from 'rxjs';
import { RouteHelperService } from '@app/shared/services/route-helper.service';
import { AriaLivePoliteness, LiveAnnouncer } from '@angular/cdk/a11y';
import { SubnavState } from '@shared/enums';

@Component({
  selector: 'program-switcher',
  templateUrl: './program-switcher.component.html',
  styleUrls: ['./program-switcher.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ProgramSwitcherComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.scrollbar-visible')
  get scrollbarVisible(): boolean {
    return !this.isMobile && this.filteredPrograms.length > 4;
  }
  programs: { name: string; type: string }[] = [];
  filteredPrograms: { name: string; type: string }[] = [];
  public searchControl = new FormControl<string>('');

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('container', { static: true }) containerRef: ElementRef;
  @ViewChildren('programItem') programItems: QueryList<ElementRef<HTMLButtonElement>>;

  private activeItemIndex = -1;
  private readonly timers: number[] = [];

  private focusOutListener: () => void;
  private searchSub: Subscription;
  private searchInputEscapeKeyListener: () => void;
  private containerKeydownListener: () => void;

  @HostBinding('class.mobile')
  public get isMobile(): boolean {
    return this.layoutService.isMobileScreen;
  }

  constructor(
    private readonly tokenStorage: TokenStorageService,
    private readonly layoutService: LayoutService,
    private readonly routeHelper: RouteHelperService,
    private readonly renderer: Renderer2,
    private readonly liveAnnouncer: LiveAnnouncer
  ) {}

  ngOnInit(): void {
    const userInfo = this.tokenStorage.userInfo;
    if (userInfo?.AvailablePrograms) {
      this.programs = userInfo.AvailablePrograms.map((p) => ({
        name: p.Name,
        type: CustomUtils.getProgramType(p.Type)
      }));
      this.filteredPrograms = [...this.programs];
      // Announce initial count
      this.announceResults(this.filteredPrograms.length);
    }
    this.searchSub = this.searchControl.valueChanges.subscribe((term) => {
      const lower = (term || '').toLowerCase();
      this.filteredPrograms = this.programs.filter(
        (p) => p.name.toLowerCase().includes(lower) || p.type.toLowerCase().includes(lower)
      );
      this.activeItemIndex = -1;
      this.announceResults(this.filteredPrograms.length);
    });
  }

  private announceResults(count: number, politeNess: AriaLivePoliteness = 'polite'): void {
    if (count === 0) {
      this.liveAnnouncer.announce('No results found', politeNess);
    } else {
      this.liveAnnouncer.announce(`${count} results found`, 'polite');
    }
  }

  private getProgramItems(): ElementRef<HTMLButtonElement>[] {
    return this.programItems?.toArray() ?? [];
  }

  private focusFirstProgramItem(): void {
    const items = this.getProgramItems();
    if (items.length > 0) {
      this.activeItemIndex = 0;
      items[0].nativeElement.focus();
    }
  }

  private addEscapeKeyHandlerForSearchInput(): void {
    const searchInput = this.searchInput['searchInput'].nativeElement;

    this.searchInputEscapeKeyListener = this.renderer.listen(searchInput, 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.layoutService.showProgramSwitcher = false;
      }
    });
  }

  public onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.layoutService.showProgramSwitcher = false;
      return;
    }

    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      const clearButton = this.containerRef.nativeElement.querySelector(
        'neo-input-search div[role="button"]'
      ) as HTMLElement;
      if (clearButton && document.activeElement === clearButton) {
        this.searchInput['searchInput'].nativeElement.focus();
        return;
      }
      if (this.getProgramItems().length > 0) {
        this.activeItemIndex = this.getProgramItems().length - 1;
        this.updateFocusedItem();
      }
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      if (!this.searchControl.value) {
        if (this.getProgramItems().length > 0) {
          this.activeItemIndex = 0;
          this.updateFocusedItem();
        }
        event.stopPropagation();
        return;
      } else {
        const clearButton = this.containerRef.nativeElement.querySelector(
          'neo-input-search div[role="button"]'
        ) as HTMLElement;

        if (clearButton && document.activeElement === clearButton) {
          if (this.getProgramItems().length > 0) {
            this.activeItemIndex = 0;
            this.updateFocusedItem();
          }
          event.stopPropagation();
          return;
        }
        if (clearButton) {
          clearButton.focus();
          return;
        }
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.getProgramItems().length > 0) {
        this.activeItemIndex = 0;
        this.updateFocusedItem();
      }
    }
  }

  public onListKeydown(event: KeyboardEvent): void {
    const items = this.getProgramItems();
    if (items.length === 0) return;

    const currentFocusedIndex = items.findIndex((item) => item.nativeElement === document.activeElement);
    if (currentFocusedIndex !== -1) {
      this.activeItemIndex = currentFocusedIndex;
    }

    if (['ArrowDown', 'ArrowUp', 'Tab', 'Enter'].includes(event.key)) {
      event.preventDefault();
    }

    switch (event.key) {
      case 'ArrowDown':
      case 'Tab':
        if (event.shiftKey) {
          if (this.activeItemIndex === 0) {
            return this.focusSearchInputOrClear();
          }
          this.activeItemIndex--;
        } else {
          if (this.activeItemIndex === items.length - 1) {
            this.searchInput['searchInput'].nativeElement.focus();
            return;
          }
          this.activeItemIndex++;
        }
        break;

      case 'ArrowUp':
        this.activeItemIndex--;
        if (this.activeItemIndex < 0) {
          this.searchInput['searchInput'].nativeElement.focus();
          return;
        }
        break;

      case 'Enter':
        if (this.activeItemIndex >= 0) {
          items[this.activeItemIndex].nativeElement.click();
        }
        return;

      default:
        return;
    }

    if (this.activeItemIndex < 0) {
      this.activeItemIndex = items.length - 1;
    }
    if (this.activeItemIndex >= items.length) {
      this.activeItemIndex = 0;
    }

    this.updateFocusedItem();
  }

  private focusSearchInputOrClear() {
    const clearButton = this.containerRef.nativeElement.querySelector(
      'neo-input-search div[role="button"]'
    ) as HTMLElement;
    if (clearButton) {
      clearButton.focus();
    } else {
      this.searchInput['searchInput'].nativeElement.focus();
    }
  }

  public closeSwitcher(programWasSelected: boolean): void {
    if (programWasSelected) {
      this.layoutService.focusAfterProgramChange = true;
    }
    this.layoutService.showProgramSwitcher = false;
  }

  public onItemFocus(event: FocusEvent): void {
    this.renderer.addClass(event.target, 'keyboard-focused');
  }

  public onItemBlur(event: FocusEvent): void {
    this.renderer.removeClass(event.target, 'keyboard-focused');
  }

  private updateFocusedItem(): void {
    const items = this.getProgramItems();
    const activeItem = items[this.activeItemIndex];
    if (activeItem) {
      activeItem.nativeElement.focus();
    }
  }

  public onSearch(term: string) {
    this.searchControl.setValue(term);
  }

  public ngAfterViewInit(): void {
    if (this.searchInput['searchInput']?.nativeElement) {
      const timer = window.setTimeout(() => {
        this.searchInput['searchInput'].nativeElement.focus();
        this.addEscapeKeyHandlerForSearchInput();
      }, 400);
      this.timers.push(timer);
    }

    const container = this.containerRef.nativeElement;
    this.focusOutListener = () => {
      const timer = window.setTimeout(() => {
        if (!this.layoutService.showProgramSwitcher) {
          return;
        }
        const active = document.activeElement as HTMLElement;
        if (container && !container.contains(active)) {
          this.focusFirstProgramItem();
        }
        const programsList = container.querySelector('.programs-list');
        if (programsList && active === programsList) {
          this.focusFirstProgramItem();
        }
      }, 0);
      this.timers.push(timer);
    };
    container.addEventListener('focusout', this.focusOutListener);

    this.containerKeydownListener = this.renderer.listen(
      this.containerRef.nativeElement,
      'keydown',
      (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.layoutService.showProgramSwitcher = false;
        }
      }
    );
  }

  public clearSearch(): void {
    this.searchControl.setValue('');
    this.announceResults(this.programs.length, 'assertive');
  }

  public selectProgram(program: { name: string; type: string }): void {
    const userInfo = this.tokenStorage.userInfo;
    if (userInfo?.AvailablePrograms) {
      const selectedProgram = userInfo.AvailablePrograms.find((p) => p.Name === program.name);
      if (selectedProgram) {
        this.routeHelper.changeCurrent(this.tokenStorage.userInfo.CurrentSiteId, selectedProgram.Id);

        this.closeSwitcher(true);
        if (this.layoutService.isMobileScreen) {
          this.layoutService.subnavState = SubnavState.hidden;
        }
      }
    }
  }

  public isProgramSelected(program: { name: string; type: string }): boolean {
    const userInfo = this.tokenStorage.userInfo;
    if (!userInfo?.AvailablePrograms || !userInfo.CurrentProgramId) return false;
    const selected = userInfo.AvailablePrograms.find((p) => p.Name === program.name);
    return selected && selected.Id === userInfo.CurrentProgramId;
  }

  ngOnDestroy(): void {
    if (this.focusOutListener) {
      this.containerRef.nativeElement.removeEventListener('focusout', this.focusOutListener);
    }
    if (this.searchSub) {
      this.searchSub.unsubscribe();
    }
    if (this.searchInputEscapeKeyListener) {
      this.searchInputEscapeKeyListener();
    }
    if (this.containerKeydownListener) {
      this.containerKeydownListener();
    }
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.length = 0;
    this.programs = [];
    this.filteredPrograms = [];
  }
}
